import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DonneesStructureBasePourParois,
  MotifPersonnaliseParoi,
  PARAMETRES_PAROIS_DEFAUT,
  ParoisState,
  ParametresParoi,
  ParametresParois,
  StructureParois,
  TypeFixationParoi,
  TypeMateriauParoi,
  TypeMotifParoi
} from '../types';
import { Parois3DService } from '../services/Parois3DService';
import './ParoisModule.css';

interface ParoisModuleProps {
  donneesStructureBase: DonneesStructureBasePourParois;
  onStructureChange?: (structure: StructureParois | null) => void;
  onError?: (error: string) => void;
}

export const ParoisModule: React.FC<ParoisModuleProps> = ({
  donneesStructureBase,
  onStructureChange,
  onError
}) => {
  const [state, setState] = useState<ParoisState>({
    parametres: {
      ...PARAMETRES_PAROIS_DEFAUT,
      parois: [
        {
          id: 'paroi-avant',
          nom: 'Paroi',
          materiau: TypeMateriauParoi.PANNEAU_COMPOSITE,
          fixation: TypeFixationParoi.PERMANENT,
          motif: TypeMotifParoi.PLEIN,
          hauteur: donneesStructureBase?.hauteur ?? 2.0,
          largeur: donneesStructureBase?.largeur ?? 2.0,
          epaisseur: 0.02,
          couleur: '#8B7355',
          transparence: 0.0,
          decalageSol: 0,
          actif: false
        }
      ]
    },
    isGenerating: false,
    editeurActif: false
  });

  const serviceRef = useRef<Parois3DService | null>(null);
  const onStructureChangeRef = useRef(onStructureChange);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onStructureChangeRef.current = onStructureChange;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    if (!serviceRef.current) {
      serviceRef.current = new Parois3DService();
    }
    return () => {
      serviceRef.current?.dispose();
      serviceRef.current = null;
    };
  }, []);

  const genererParois = useCallback((params: ParametresParois) => {
    if (!serviceRef.current) return;
    setState(prev => ({ ...prev, isGenerating: true, error: undefined }));
    try {
      const structure = serviceRef.current.genererParois(params, donneesStructureBase);
      setState(prev => ({ ...prev, structure, isGenerating: false }));
      onStructureChangeRef.current?.(structure);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Erreur inconnue';
      setState(prev => ({ ...prev, error: message, isGenerating: false }));
      onErrorRef.current?.(message);
    }
  }, [donneesStructureBase]);

  // Regénérer si les paramètres ou la structure de base changent
  const prevParamsRef = useRef<ParametresParois | null>(null);
  const prevBaseRef = useRef<any>(null);

  useEffect(() => {
    const current = state.parametres;
    const prev = prevParamsRef.current;
    const basePrev = prevBaseRef.current;

    const paramsChanged = !prev || JSON.stringify(prev) !== JSON.stringify(current);
    const baseChanged = !basePrev ||
      basePrev.largeur !== donneesStructureBase.largeur ||
      basePrev.longueur !== donneesStructureBase.longueur ||
      basePrev.hauteur !== donneesStructureBase.hauteur ||
      basePrev.nombreMontants !== donneesStructureBase.nombreMontants;

    if (current.actif && (paramsChanged || baseChanged)) {
      prevParamsRef.current = { ...current };
      prevBaseRef.current = { ...donneesStructureBase };
      genererParois(current);
    } else if (!current.actif) {
      onStructureChangeRef.current?.(null);
    }
  }, [state.parametres, donneesStructureBase, genererParois]);

  // UI Handlers
  const toggleActif = useCallback((checked: boolean) => {
    setState(prev => ({
      ...prev,
      parametres: { ...prev.parametres, actif: checked }
    }));
  }, []);

  const majParoi = useCallback((id: string, patch: Partial<ParametresParoi>) => {
    setState(prev => ({
      ...prev,
      parametres: {
        ...prev.parametres,
        parois: prev.parametres.parois.map(p => p.id === id ? { ...p, ...patch } : p)
      }
    }));
  }, []);

  const ajouterParoi = useCallback(() => {
    const n = state.parametres.parois.length + 1;
    const nouvelle: ParametresParoi = {
      id: `paroi-${n}`,
      nom: `Paroi ${n}`,
      materiau: TypeMateriauParoi.PANNEAU_BOIS,
      fixation: TypeFixationParoi.AMOVIBLE,
      motif: TypeMotifParoi.PLEIN,
      hauteur: donneesStructureBase.hauteur,
      largeur: donneesStructureBase.longueur,
      epaisseur: 0.02,
      couleur: '#8B7355',
      transparence: 0,
      decalageSol: 0,
      actif: true
    };
    setState(prev => ({
      ...prev,
      parametres: { ...prev.parametres, parois: [...prev.parametres.parois, nouvelle] }
    }));
  }, [state.parametres.parois.length, donneesStructureBase]);

  const retirerParoi = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      parametres: { ...prev.parametres, parois: prev.parametres.parois.filter(p => p.id !== id) }
    }));
  }, []);

  return (
    <div className="parois-module">
      <div className="parois-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong>Module 7: Les Parois</strong>
        </div>
        <label className="parois-switch">
          <input type="checkbox" checked={state.parametres.actif} onChange={e => toggleActif(e.target.checked)} />
          <span>Activer</span>
        </label>
      </div>

      {state.isGenerating && (
        <div className="parois-status">⏳ Génération des parois...</div>
      )}
      {!!state.error && (
        <div className="parois-error">❌ {state.error}</div>
      )}

      <div className="parois-controls">
        <div className="parois-field">
          <label>Hauteur par défaut (m)</label>
          <input type="number" step={0.1} value={state.parametres.hauteurDefaut}
                 onChange={e => setState(prev => ({ ...prev, parametres: { ...prev.parametres, hauteurDefaut: parseFloat(e.target.value) } }))} />
        </div>
        <div className="parois-field">
          <label>Épaisseur par défaut (m)</label>
          <input type="number" step={0.01} value={state.parametres.epaisseurDefaut}
                 onChange={e => setState(prev => ({ ...prev, parametres: { ...prev.parametres, epaisseurDefaut: parseFloat(e.target.value) } }))} />
        </div>
        <div className="parois-field">
          <label>Matériau par défaut</label>
          <select value={state.parametres.materiauDefaut}
                  onChange={e => setState(prev => ({ ...prev, parametres: { ...prev.parametres, materiauDefaut: e.target.value as TypeMateriauParoi } }))}>
            {Object.values(TypeMateriauParoi).map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className="parois-field">
          <label>Motif par défaut</label>
          <select value={state.parametres.motifDefaut}
                  onChange={e => setState(prev => ({ ...prev, parametres: { ...prev.parametres, motifDefaut: e.target.value as TypeMotifParoi } }))}>
            {Object.values(TypeMotifParoi).map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="parois-list">
        {state.parametres.parois.map((p) => (
          <div key={p.id} className="parois-item">
            <div className="parois-row" style={{ justifyContent: 'space-between' }}>
              <strong>{p.nom}</strong>
              <label className="parois-switch">
                <input type="checkbox" checked={p.actif} onChange={e => majParoi(p.id, { actif: e.target.checked })} />
                <span>Actif</span>
              </label>
            </div>

            <div className="parois-row">
              <div className="parois-field" style={{ flex: 1 }}>
                <label>Fixation</label>
                <select value={p.fixation} onChange={e => majParoi(p.id, { fixation: e.target.value as TypeFixationParoi })}>
                  {Object.values(TypeFixationParoi).map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
              <div className="parois-field" style={{ flex: 1 }}>
                <label>Matériau</label>
                <select value={p.materiau} onChange={e => majParoi(p.id, { materiau: e.target.value as TypeMateriauParoi })}>
                  {Object.values(TypeMateriauParoi).map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="parois-row">
              <div className="parois-field" style={{ flex: 1 }}>
                <label>Matériau</label>
                <select value={p.materiau} onChange={e => majParoi(p.id, { materiau: e.target.value as TypeMateriauParoi })}>
                  {Object.values(TypeMateriauParoi).map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="parois-field" style={{ flex: 1 }}>
                <label>Motif</label>
                <select value={p.motif} onChange={e => majParoi(p.id, { motif: e.target.value as TypeMotifParoi })}>
                  {Object.values(TypeMotifParoi).map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="parois-row">
              <div className="parois-field" style={{ flex: 1 }}>
                <label>Largeur (m)</label>
                <input type="number" step={0.1} value={p.largeur} onChange={e => majParoi(p.id, { largeur: parseFloat(e.target.value) })} />
              </div>
              <div className="parois-field" style={{ flex: 1 }}>
                <label>Hauteur (m)</label>
                <input type="number" step={0.1} value={p.hauteur} onChange={e => majParoi(p.id, { hauteur: parseFloat(e.target.value) })} />
              </div>
            </div>

            <div className="parois-row">
              <div className="parois-field" style={{ flex: 1 }}>
                <label>Épaisseur (m)</label>
                <input type="number" step={0.01} value={p.epaisseur} onChange={e => majParoi(p.id, { epaisseur: parseFloat(e.target.value) })} />
              </div>
              <div className="parois-field" style={{ flex: 1 }}>
                <label>Couleur</label>
                <input type="color" value={p.couleur} onChange={e => majParoi(p.id, { couleur: e.target.value })} />
              </div>
            </div>

            <div className="parois-row">
              <div className="parois-field" style={{ flex: 1 }}>
                <label>Transparence (0-1)</label>
                <input type="number" step={0.1} min={0} max={1} value={p.transparence} onChange={e => majParoi(p.id, { transparence: parseFloat(e.target.value) })} />
              </div>
              <div className="parois-field" style={{ flex: 1 }}>
                <label>Décalage sol (m)</label>
                <input type="number" step={0.05} value={p.decalageSol} onChange={e => majParoi(p.id, { decalageSol: parseFloat(e.target.value) })} />
              </div>
            </div>

            <div className="parois-row">
              <div className="parois-field" style={{ flex: 1 }}>
                <label>Répartition (toutes les X entre-montants)</label>
                <input type="number" min={1} step={1} value={p.pasEntreMontants ?? 1}
                  onChange={e => majParoi(p.id, { pasEntreMontants: Math.max(1, Math.floor(Number(e.target.value) || 1)) })} />
              </div>
            </div>

            <div className="parois-actions">
              <button className="parois-button" onClick={() => majParoi(p.id, { actif: !p.actif })}>{p.actif ? 'Désactiver' : 'Activer'}</button>
              <button className="parois-button" onClick={() => retirerParoi(p.id)}>Supprimer</button>
            </div>
          </div>
        ))}
      </div>

      <div className="parois-actions">
        <button className="parois-button" onClick={ajouterParoi}>+ Ajouter une paroi</button>
        <button className="parois-button" onClick={() => state.parametres.actif && genererParois(state.parametres)}>Régénérer</button>
      </div>
    </div>
  );
};

export default ParoisModule;